import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import IndicatorsHeader from './IndicatorsHeader';
import { useClient } from 'shared/apollo';
import {
  IndicatorDetailsRoutes,
  SectionIndicators,
  sortIndicatorsIntoSections,
  initialEmptyData,
  getFirstSectionKey,
  ReportAPIResponse,
  mapIndicators,
  mapIndicatorsById,
  IndicatorDetails,
} from './utils';
import IndicatorDetailsFooter from './IndicatorDetailsFooter';
import IndicatorRoutes from './IndicatorRoutes';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import {
  IndicatorSectionKey,
  isImpactDetailsCompleted,
} from '../ImpactIndicator/utils';
import { scrollToTop } from 'shared/utils/pageUtils';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { getReportIndicators } from './api';
import { useSaveReportIndicators } from 'shared/providers/IndicatorProvider/hooks';
import { ReportPeriod, parsePeriodValuesString } from '../SectionPeriod/utils';
import IndicatorCompletedRender from './IndicatorDetailQuestions/IndicatorCompletedRender';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { faBrakeWarning } from '@fortawesome/pro-light-svg-icons';
import { GuidanceTextResponse } from '../utils';

export interface SectionIndicatorDetailsProps {
  reportId: number | string;
  saving?: boolean;
  programName: string;
  isReportPassed: boolean;
  reportDates?: string;
  allIndicators: ReturnType<typeof mapIndicatorsById>;
  intermediaries?: string;
  serviceGoal?: string;
  serviceType?: string;
  beneficiaryTerm?: string;
  capacityDevelopmentEnabled: boolean;
  learnEnabled: boolean;
  actEnabled: boolean;
  goToDateSection: () => unknown;
  reloadAnswers: () => unknown;
  onNextSection: () => unknown;
  onPreviousSection: () => unknown;
  handleNext: (buttonPressed: boolean, answer?: string) => unknown;
}

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  contain: paint;
  .content {
    padding-left: ${SPACERS.extraLarge};
    padding-right: ${SPACERS.extraLarge};
    padding-bottom: ${SPACERS.extraLarge};
    max-width: 984px;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
    margin-top: 48px;
    margin-bottom: 48px;
    flex: 1;
    overflow: auto;
    word-break: break-word;
    .heading-helper-text {
      max-width: 400px;
    }
  }
`;

const SectionIndicatorDetails: React.FC<SectionIndicatorDetailsProps & {
  value?: string;
  guidanceText: GuidanceTextResponse;
}> = ({
  saving,
  reportId,
  guidanceText,
  isReportPassed,
  programName,
  intermediaries,
  serviceGoal,
  serviceType,
  allIndicators,
  reportDates,
  beneficiaryTerm,
  capacityDevelopmentEnabled,
  learnEnabled,
  actEnabled,
  goToDateSection,
  reloadAnswers,
  handleNext,
  onNextSection: onNextReportBuilderSection,
  onPreviousSection,
}) => {
  const reportPeriod: ReportPeriod = reportDates
    ? typeof reportDates === 'string'
      ? parsePeriodValuesString(reportDates)
      : reportDates
    : {
        card: undefined,
        startDate: undefined,
        endDate: undefined,
      };
  const client = useClient();
  const [reportRequestState, makeReportRequest] = useGraphQLRequest<{
    allReports: [ReportAPIResponse];
  }>();
  const [
    saveReportIndicators,
    saveRequestState,
    setSaveRequestState,
  ] = useSaveReportIndicators();

  const [indicators, setIndicators] = useState<SectionIndicators>(
    initialEmptyData,
  );

  const [selectedSectionKey, setSelectedSectionKey] = useState<
    IndicatorSectionKey
  >();
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [route, setRoute] = useState<IndicatorDetailsRoutes>('intro');

  useEffect(() => {
    makeReportRequest(getReportIndicators(client!, reportId));
  }, [reportId]);

  const handleSelectIndicator = useCallback(
    (
      sectionKey: IndicatorSectionKey,
      index: number,
      route: IndicatorDetailsRoutes = 'forecast',
    ) => {
      setSelectedSectionKey(sectionKey);
      setSelectedIndex(index);
      setRoute(route);
    },
    [setSelectedSectionKey, setSelectedIndex, setRoute],
  );

  useEffect(() => {
    if (reportRequestState.type === 'REQUEST_SUCCESS') {
      const rawIndicators = mapIndicators(
        reportRequestState,
        capacityDevelopmentEnabled,
        learnEnabled,
        actEnabled,
        isReportPassed,
      );
      const indicatorSections = sortIndicatorsIntoSections(rawIndicators);
      setIndicators(indicatorSections);
      const {
        firstSectionKey,
        sectionKey,
        indicatorIndex,
        questionKey,
      } = getFirstSectionKey(indicatorSections, isReportPassed);
      setSelectedSectionKey(sectionKey);
      setSelectedIndex(indicatorIndex);
      setRoute(
        firstSectionKey === sectionKey &&
          questionKey === 'forecast' &&
          indicatorIndex === 0
          ? 'intro'
          : questionKey,
      );
    }
  }, [
    reportRequestState.type,
    setSelectedSectionKey,
    setSelectedIndex,
    setIndicators,
    setRoute,
  ]);

  useEffect(() => {
    scrollToTop();
  }, [route]);

  const handleIndicatorSave = useCallback(
    (i: IndicatorDetails) => {
      saveReportIndicators({
        reportId: parseInt(reportId as string),
        file: i.file || undefined,
        forecast: parseInt((i.forecast || '').toString().replace(/,/g, '')),
        finalForecast: parseInt(
          (i.finalForecast || '').toString().replace(/,/g, ''),
        ),
        interventionDosageLevel: i.interventionDosageLevel,
        interventionDosageType: i.interventionDosageType,
        interventionDosageNumber: i.interventionDosageNumber,
        timePeriod: i.timePeriod,
        indicatorId: i.indicatorId,
        measurement: i.measurement,
        happensAfterProgramYear: i.happensAfterProgramYear,
      });
    },
    [reportId, saveReportIndicators],
  );

  if (!selectedSectionKey) return <TrueImpactLoading />;

  if (!reportRequestState.data?.allReports[0].indicators.length) {
    return (
      <StyledDiv>
        <IndicatorsHeader
          route={route}
          guidanceText={guidanceText}
          indicators={indicators}
          selectedIndex={selectedIndex}
          isReportPassed={isReportPassed}
          programName={programName}
          intermediaries={intermediaries}
          serviceGoal={serviceGoal}
          serviceType={serviceType}
          selectedSectionKey={selectedSectionKey}
          handleSelectIndicator={handleSelectIndicator}
        />
        <div className="content">
          <IndicatorCompletedRender
            icon={faBrakeWarning}
            iconColor="warning"
            heading="Impact model was updated!"
            subheading={'Looks like your Impact model was updated. Please go through all the questions in Impact Model Builder section ' +
              'before starting this section. Don\'t worry your data will not be lost.'}
            children={
              <div className="go-to-impact-summary-button">
                <TrueImpactButton onClick={onPreviousSection}>
                  Go to Impact Model Builder
                </TrueImpactButton>
              </div>
            }
          />
        </div>
      </StyledDiv>
    );
  }

  const isCompleted = isImpactDetailsCompleted(
    indicators,
    capacityDevelopmentEnabled,
    learnEnabled,
    actEnabled,
    isReportPassed,
  );

  return (
    <StyledDiv>
      <IndicatorsHeader
        route={route}
        guidanceText={guidanceText}
        indicators={indicators}
        selectedIndex={selectedIndex}
        isReportPassed={isReportPassed}
        programName={programName}
        intermediaries={intermediaries}
        serviceGoal={serviceGoal}
        serviceType={serviceType}
        selectedSectionKey={selectedSectionKey}
        handleSelectIndicator={isCompleted ? handleSelectIndicator : undefined}
      />
      <div className="content">
        <IndicatorRoutes
          route={route}
          guidanceText={guidanceText}
          reportPeriod={reportPeriod}
          setRoute={setRoute}
          indicators={indicators}
          programName={programName}
          allIndicators={allIndicators}
          intermediaries={intermediaries}
          serviceGoal={serviceGoal}
          serviceType={serviceType}
          beneficiaryTerm={beneficiaryTerm}
          handleNext={handleNext}
          setIndicators={setIndicators}
          reloadAnswers={reloadAnswers}
          handleIndicatorSave={handleIndicatorSave}
          isReportPassed={isReportPassed}
          saveRequestState={saveRequestState}
          setSaveRequestState={setSaveRequestState}
          goToDateSection={goToDateSection}
          selectedSectionKey={selectedSectionKey}
          setSelectedSectionKey={setSelectedSectionKey}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          onPrevious={onPreviousSection}
          onNextReportBuilderSection={onNextReportBuilderSection}
          reportId={reportId}
          saving={saving || saveRequestState.type === 'REQUEST_START'}
        />
      </div>
      <IndicatorDetailsFooter
        route={route}
        indicators={indicators}
        selectedSectionKey={selectedSectionKey}
        selectedIndex={selectedIndex}
        hideFooter={true}
      />
    </StyledDiv>
  );
};

export default SectionIndicatorDetails;
